import '@assets/scss/app.scss'
import { string } from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { useEffect } from 'react'
import { mainFullUpdate, updateNotification } from '@store/slice/mainSlice'
import { useNavigate } from 'react-router'
import { ROLE_PUBLIC } from '@router/Routes'
import { post, apiTokenId } from '@util/xhrAPI'
import Div from '@ui/layout/Div'
import Dashboard from '@scene/window/Dashboard'
import Header from '@skeleton/Header'
import Footer from '@skeleton/Footer'
import CreateMission from '@scene/window/mission/CreateMission'
import DynamicForm from '@scene/modal/DynamicForm'
import Home from '@scene/window/Home'
import Login from '@scene/window/security/Login'
import CitizenSterilizationCallMaker from '@scene/window/sterilization/CitizenSterilizationCallMaker'
import CompanySterilizationCallMaker from '@scene/window/sterilization/CompanySterilizationCallMaker'
import MissionsList from '@scene/window/mission/MissionsList'
import ListUsers from '@scene/window/users/ListUsers'
import ListDocument from '@scene/window/document/ListDocument'
import useFormData from '@hook/useFormData'
import Loader from '@ui/elements/Loader'
import useRestrictRoute from '@hook/useRestrictRoute'
import Alert from '@ui/elements/Alert'
import UpdateUserPassword from '@scene/window/users/UpdateUserPassword'
import CreateUser from '@scene/window/users/CreateUser'
import Profile from '@scene/window/users/Profile'
import ListDistricts from '@scene/window/district/ListDistricts'
import MissionContacts from '@scene/window/mission/MissionContacts'
import SterilizationsList from '@scene/window/sterilization/SterilizationsList'
import SterilizationCallShow from '@scene/window/sterilization/SterilizationCallShow'
import ListPages from '@scene/window/page/ListPages'
import ListMailTemplates from '@scene/window/mail-template/ListMailTemplates'
import ValidationPage from '@scene/window/validation-page/ValidationPage'
import EmailValidation from '@scene/window/users/EmailValidation'
import ScanJourney from '@scene/window/pilot/ScanJourney'
import MissionPilots from '@scene/window/mission-pilots/MissionPilots'
import PrivacyPolicyPage from '@scene/window/page/PrivacyPolicyPage'
import ManagePermissions from '@scene/window/permission/ManagePermissions'
import UserRoleSubjectAuthorizations from '@scene/window/permission/__partials/UserRoleSubjectAuthorizations'
import MissionPreparation from '@scene/window/mission-preparation/MissionPreparation'
import ListMissionBillings from '@scene/window/mission-billings/ListMissionBillings'

const GlobalScene = ({ scene }) => {
  const { initialied, logguedUser, notification, homePage, currentPage, mission } = useSelector(state => state.main)
  const { specialStyle } = mission
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const renderFormData = useFormData()
  const isUserAuthorized = useRestrictRoute()
  const logguedUserRole = logguedUser ? logguedUser.roles : 'ROLE_PUBLIC'

  const windows = {
    home: <Home />,
    login: <Login />,
    createMission: <CreateMission />,
    dashboard: <Dashboard />,
    citizenSterilizationCall: <CitizenSterilizationCallMaker />,
    companySterilizationCall: <CompanySterilizationCallMaker />,
    missions: <MissionsList />,
    users: <ListUsers />,
    listDocuments: <ListDocument />,
    createUser: <CreateUser />,
    updateUser: <Profile />,
    security: <UpdateUserPassword />,
    profile: <Profile />,
    districts: <ListDistricts />,
    missionContacts: <MissionContacts />,
    sterilizations: <SterilizationsList />,
    sterilizationShow: <SterilizationCallShow />,
    pages: <ListPages />,
    mailTemplates: <ListMailTemplates />,
    publicValidation: <ValidationPage />,
    emailValidation: <EmailValidation />,
    scanJourney: <ScanJourney />,
    missionPilots: <MissionPilots />,
    privacyPage: <PrivacyPolicyPage />,
    managePermissions: <ManagePermissions />,
    manageAuthorizations: <UserRoleSubjectAuthorizations />,
    missionPreparation: <MissionPreparation />,
    missionBilling: <ListMissionBillings />
  }

  const checkPrivilege = () => {
    if (initialied && !isUserAuthorized(scene, logguedUserRole)) {
      navigate(ROLE_PUBLIC['home'])
    }
  }

  const getScene = () => {
    if ('undefined' !== typeof windows[scene]) {
      checkPrivilege()
      return windows[scene]
    }
    return <></>
  }

  const getNotification = () => {
    if (notification) {
      setTimeout(() => {
        dispatch(updateNotification(null))
      }, 3000)
      return <Alert type={notification.type} message={notification.message} cssClass='global-notification' />
    }
  }

  const scrollToTop = () => {
    window.scrollTo({ top: document.querySelector('.main-content').offsetTop, behavior: 'smooth' })
  }

  const scrollToTopButtonListener = () => {
    window.addEventListener('scroll', () => {
      var hiddenPart = document.querySelector('#root').getBoundingClientRect().top * -1;
      var windowHeight = self.innerHeight;
      if (windowHeight > hiddenPart && !document.getElementById('toTop').classList.contains('is-hidden')) {
        document.getElementById("toTop").classList.add('is-hidden');
      }
      if (windowHeight < hiddenPart && document.getElementById('toTop').classList.contains('is-hidden')) {
        document.getElementById("toTop").classList.remove('is-hidden');
      }
    }, true)
  }

  useEffect(() => {
    if (initialied === false) {
      const initRequest = { missionUrl: window.location.hostname }
      if (null === logguedUser) {
        initRequest.withLogguedUser = true
      }
      post(`${import.meta.env.VITE_API_URL}/missions/init`, renderFormData(initRequest),
        response => {
          const { mission, errors, logguedUser, homePage, privacyPage, rgpdPage } = response
          if (!errors.length) {
            const action = { mission, initialied: true, homePage, privacyPage, rgpdPage }
            if ('undefined' !== typeof logguedUser) {
              action.logguedUser = logguedUser
            }
            dispatch(mainFullUpdate(action))
          }
        })
    }
  }, [initialied])

  useEffect(() => {
    if (!window.localStorage.getItem(apiTokenId) && logguedUserRole !== 'ROLE_PUBLIC') {
      navigate(ROLE_PUBLIC['login'])
    }
    checkPrivilege()
    scrollToTopButtonListener()
  }, [])

  useEffect(() => {
    if (homePage || currentPage) {
      const metaTitle = currentPage ? currentPage.title : homePage.title
      const metaDescription = currentPage ? currentPage.fullTitle : homePage.fullTitle

      document.title = metaTitle
      document.querySelector('meta[name="description"]').setAttribute("content", metaDescription)
    }
  }, [homePage, currentPage])

  return (
    <>
      {
        initialied
          ? <>
            {scene !== 'login' && <Header />}
            <Div cssClass={`${scene} main-content ${scene !== 'login' ? 'container' : ''}`}>
              {getScene()}
              {getNotification()}
            </Div>
            {scene !== 'login' && <Footer />}
            {scene !== 'login' ? <DynamicForm /> : <></>}
            <button id="toTop" className="is-hidden" onClick={scrollToTop}>
              <svg viewBox="0 0 24 24">
                <path d="M13,20H11V8L5.5,13.5L4.08,12.08L12,4.16L19.92,12.08L18.5,13.5L13,8V20Z"></path>
              </svg>
            </button>
            <style>
              {specialStyle}
            </style>
          </>
          : <Loader cssClass='loader-bis' />
      }
    </>
  )
}

GlobalScene.propTypes = {
  scene: string
}

export default GlobalScene